import React, { useState, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudUploadAlt,
  faTimes,
  faDownload,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../../config';
import { showToast } from '../../components/toast';

const AdsReportImportModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelected(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelected(e.target.files[0]);
    }
  };

  const handleFileSelected = (selectedFile) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileName = selectedFile.name.toLowerCase();
    const isValid = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValid) {
      showToast.error('Please select a valid Excel (.xlsx, .xls) or CSV (.csv) file.');
      return;
    }

    setFile(selectedFile);
    setImportResult(null);
  };

  const handleUpload = async () => {
    if (!file) {
      showToast.error('Please choose a file to upload.');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API_URL}report-ads/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data?.success) {
        setImportResult(res.data.data);
        showToast.success(res.data.message || 'Ads file imported successfully!');
        if (onImportSuccess) {
          onImportSuccess();
        }
      }
    } catch (err) {
      console.error('Import error', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to import file.';
      showToast.error(errMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      setDownloadingTemplate(true);
      const token = localStorage.getItem('authToken');
      const res = await axios.get(`${API_URL}report-ads/sample-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sample_ads_report_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Sample download error', err);
      showToast.error('Failed to download sample file.');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
        onClick={onClose}
      >
        <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="modal-title" style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                Import Ads Report Data
              </h5>
              <button
                type="button"
                className="close"
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  lineHeight: '1',
                  cursor: 'pointer',
                  color: '#6c757d',
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              {importResult ? (
                <div style={{ textAlign: 'center', padding: '15px 0' }}>
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 44, color: '#28a745', marginBottom: 14 }} />
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>Import Completed!</h4>
                  <p style={{ color: '#495057', fontSize: 13.5 }}>
                    Processed <strong>{importResult.totalRowsProcessed}</strong> rows. Inserted{' '}
                    <strong style={{ color: '#28a745' }}>{importResult.inserted}</strong> records successfully.
                  </p>
                  {importResult.skipped > 0 && (
                    <div
                      className="alert alert-warning"
                      style={{
                        padding: '8px 12px',
                        marginTop: 12,
                        fontSize: 12.5,
                        textAlign: 'left',
                        marginBottom: 0,
                      }}
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 6 }} />
                      {importResult.skipped} rows skipped due to missing date/platform/campaign.
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Drag and Drop Zone */}
                  <div
                    className={`ads-dropzone ${isDragging ? 'active' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: '2px dashed #007bff',
                      borderRadius: '6px',
                      padding: '24px 15px',
                      textAlign: 'center',
                      background: isDragging ? '#eaf4ff' : '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept=".xlsx,.xls,.csv"
                      style={{ display: 'none' }}
                    />
                    <div style={{ fontSize: '32px', color: '#007bff', marginBottom: '8px' }}>
                      <FontAwesomeIcon icon={faCloudUploadAlt} />
                    </div>
                    {file ? (
                      <div>
                        <p style={{ color: '#007bff', fontWeight: '600', margin: '0 0 4px 0', fontSize: '13px' }}>
                          Selected: {file.name}
                        </p>
                        <small className="text-muted">Size: {(file.size / 1024).toFixed(1)} KB (Click to change)</small>
                      </div>
                    ) : (
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontWeight: '500', fontSize: '13.5px', color: '#333' }}>
                          Click or drag & drop Ads file here
                        </p>
                        <small className="text-muted">Supports Excel (.xlsx, .xls) and CSV (.csv)</small>
                      </div>
                    )}
                  </div>

                  {/* Sample Format Download Banner */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: '#eef2f7',
                      border: '1px solid #dbe2ea',
                      borderRadius: '4px',
                      padding: '10px 12px',
                      marginTop: '15px',
                    }}
                  >
                    <div style={{ fontSize: '12px', color: '#495057' }}>
                      <strong style={{ display: 'block', marginBottom: '2px', color: '#212529' }}>Need format template?</strong>
                      Includes Date, Platform, Campaign, Cost, Leads, Likely, Sales, Revenue, Month.
                    </div>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      onClick={handleDownloadSample}
                      disabled={downloadingTemplate}
                    >
                      <FontAwesomeIcon icon={downloadingTemplate ? faSpinner : faDownload} spin={downloadingTemplate} className="me-1" />
                      Sample File
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              {importResult ? (
                <>
                  <button type="button" className="btn btn-default btn-sm" onClick={handleReset}>
                    Import Another File
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>
                    Done
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-default btn-sm" onClick={onClose} disabled={uploading}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleUpload}
                    disabled={!file || uploading}
                  >
                    {uploading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCloudUploadAlt} className="me-1" />
                        Import Ads Data
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdsReportImportModal;
